import axios from "axios";
import AuthService from "../services/authService";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,

  timeout: 20000,

  /*
   * VERY IMPORTANT for HttpOnly cookies.
   */
  withCredentials: true,

  headers: {
    "Content-Type": "application/json",
  },
});

/*
 * Separate axios client for refresh.
 *
 * This client has NO refresh interceptor.
 *
 * This prevents:
 *
 * axios -> 401 -> refresh -> 401 -> refresh -> ...
 */
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,

  timeout: 20000,

  withCredentials: true,

  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;

let failedQueue = [];

let isLoggingOut = false;

/* =========================================================
   QUEUE
   ========================================================= */

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });

  failedQueue = [];
};

/* =========================================================
   AUTH ENDPOINT CHECK
   ========================================================= */

const isAuthEndpoint = (url) => {
  if (!url) return false;

  return (
    url.includes("/Login") ||
    url.includes("/RefreshToken") ||
    url.includes("/ForgotPassword") ||
    url.includes("/ResetPassword") ||
    url.includes("/Logout") ||
    url.includes("/Me")
  );
};

/* =========================================================
   REQUEST INTERCEPTOR
   ========================================================= */

axiosInstance.interceptors.request.use(
  (config) => {
    /*
     * NO Authorization header.
     *
     * Browser sends HttpOnly cookies automatically.
     */

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },

  (error) => Promise.reject(error),
);

/* =========================================================
   REFRESH
   ========================================================= */

const refreshAccessToken = async () => {
  /*
   * IMPORTANT:
   *
   * No refreshToken argument.
   *
   * Browser automatically sends:
   *
   * Cookie: refreshToken=...
   */

  await refreshClient.post("/Auth/RefreshToken", {});
};

/* =========================================================
   RESPONSE INTERCEPTOR
   ========================================================= */

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;

    /*
     * Don't refresh:
     *
     * - Login
     * - Refresh
     * - Logout
     * - Forgot password
     * - Reset password
     * - Me
     */
    if (
      status !== 401 ||
      originalRequest._retry ||
      originalRequest.skipAuthRefresh ||
      isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    /*
     * Another request is already refreshing.
     */
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve,
          reject,
        });
      })
        .then(() => {
          return axiosInstance(originalRequest);
        })
        .catch((refreshError) => {
          return Promise.reject(refreshError);
        });
    }

    originalRequest._retry = true;

    isRefreshing = true;

    try {
      /*
       * Refresh HttpOnly cookie.
       */
      await refreshAccessToken();

      /*
       * Refresh succeeded.
       */
      processQueue(null);

      /*
       * Retry original request.
       *
       * New accessToken cookie is automatically
       * sent by browser.
       */
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      /*
       * Refresh failed.
       *
       * Session is no longer valid.
       */
      processQueue(refreshError);

      performLogout();

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/* =========================================================
   LOGOUT
   ========================================================= */

function performLogout() {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  /*
   * IMPORTANT:
   *
   * We cannot delete HttpOnly cookies using JavaScript.
   *
   * Backend must clear them.
   *
   * We clear only frontend Redux state through an event.
   */
  window.dispatchEvent(new Event("auth-expired"));

  /*
   * Redirect to login.
   */
  if (
    window.location.pathname !== "/" &&
    window.location.pathname !== "/login"
  ) {
    window.location.replace("/");
  }

  setTimeout(() => {
    isLoggingOut = false;
  }, 1000);
}

export default axiosInstance;
